import type { RedoOnboardingV2RpcClient } from "@redotech/redo-onboarding-api/client";
import type { ProductResumeState } from "@redotech/redo-onboarding-api/schema/get-resume-state";
import type { ProductId } from "@redotech/redo-onboarding-api/schema/initialize";
import { useEffect, useState } from "react";

export type ProductProgressMap = Partial<Record<ProductId, ProductResumeState>>;

let sharedPromise: Promise<ProductProgressMap> | null = null;
let sharedResult: ProductProgressMap | null = null;
let sharedError: Error | null = null;
const listeners = new Set<() => void>();

function notifyListeners() {
  for (const fn of listeners) {
    fn();
  }
}

/** Kick off the resume-state fetch immediately. Call once at app startup. */
export function prefetchProductProgress(
  rpcClient: RedoOnboardingV2RpcClient | undefined,
) {
  if (!rpcClient || sharedPromise) {
    return;
  }
  sharedPromise = rpcClient
    .listResumeStates({})
    .then((data) => {
      sharedResult = data.products;
      notifyListeners();
      return sharedResult;
    })
    .catch((err) => {
      sharedError = err instanceof Error ? err : new Error(String(err));
      sharedResult = {};
      notifyListeners();
      return sharedResult;
    });
  notifyListeners();
}

/** Read product onboarding progress from the prefetched cache. */
export function useProductProgress() {
  const [result, setResult] = useState<ProductProgressMap | null>(sharedResult);
  const [loading, setLoading] = useState(!sharedResult && !!sharedPromise);
  const [error, setError] = useState<Error | null>(sharedError);

  useEffect(() => {
    function sync() {
      if (sharedResult) {
        setResult(sharedResult);
        setLoading(false);
        setError(sharedError);
      } else if (sharedError) {
        setError(sharedError);
        setResult({});
        setLoading(false);
      } else if (sharedPromise) {
        setLoading(true);
      } else {
        setLoading(false);
      }
    }

    sync();

    listeners.add(sync);
    return () => {
      listeners.delete(sync);
    };
  }, []);

  return { productProgress: result, loading, error };
}
